import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Card, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
const { ApiHelper } = require('../../utils/ApiHelper');
const { CREATE_BROADCAST, GET_BROADCASTS } = require('../../lib/API_KEYS');

const SendNotifications = () => {
  const [formData, setFormData] = useState({
    type: 'platform',
    title: '',
    message: '',
    icon: 'bell'
  });
  const [loading, setLoading] = useState(false);
  const [recentBroadcasts, setRecentBroadcasts] = useState({
    platform: [],
    admin: []
  });
  const [fetchingHistory, setFetchingHistory] = useState(true);

  useEffect(() => {
    fetchBroadcastHistory();
  }, []);

  const fetchBroadcastHistory = async () => {
    try {
      setFetchingHistory(true);
      const res = await new ApiHelper().FetchFromServer(
        ...GET_BROADCASTS,
        { limit: 5 },
      );
      
      if (res.data) {
        setRecentBroadcasts({
          platform: res.data.data.platform || [],
          admin: res.data.data.admin || []
        });
      }
    } catch (error) {
      console.error('Error fetching broadcast history:', error);
    } finally {
      setFetchingHistory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    try {
      setLoading(true);
      
      const res = await new ApiHelper().FetchFromServer(
        ...CREATE_BROADCAST,
        null,
        formData
      );

      toast.success(res.message || 'Notification broadcasted successfully!');
      
      // Reset form
      setFormData({
        type: 'platform',
        title: '',
        message: '',
        icon: 'bell'
      });
      
      // Refresh history
      fetchBroadcastHistory();
    } catch (error) {
      console.error('Error creating broadcast:', error);
      toast.error(error.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const iconOptions = [
    { value: 'bell', label: '🔔 Bell', emoji: '🔔' },
    { value: 'approved', label: '✅ Approved', emoji: '✅' },
    { value: 'rfq', label: '📋 RFQ', emoji: '📋' },
    { value: 'milestone', label: '🎯 Milestone', emoji: '🎯' },
    { value: 'payment', label: '💰 Payment', emoji: '💰' },
    { value: 'agreement', label: '✍️ Agreement', emoji: '✍️' },
  ];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container-fluid px-4 py-4">
      <div className="row">
        <div className="col-12 mb-4">
          <h3 className="mb-2">Send Notifications</h3>
          <p className="text-muted">
            Broadcast notifications to all users on the platform
          </p>
        </div>
      </div>

      <Row>
        {/* Notification Form */}
        <Col lg={6} className="mb-4">
          <Card className="shadow-sm border-0">
            <Card.Body className="p-4">
              <h5 className="mb-4">Create New Notification</h5>
              
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Notification Type *</Form.Label>
                  <Form.Select 
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    required
                  >
                    <option value="platform">Platform Update</option>
                    <option value="admin">Admin Notification</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {formData.type === 'platform' 
                      ? 'General platform updates, maintenance notices, new features'
                      : 'Important administrative announcements'}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Icon</Form.Label>
                  <Form.Select 
                    value={formData.icon}
                    onChange={(e) => handleChange('icon', e.target.value)}
                  >
                    {iconOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Title *</Form.Label>
                  <Form.Control 
                    type="text"
                    placeholder="e.g., System Maintenance Scheduled"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    required
                    maxLength={100}
                  />
                  <Form.Text className="text-muted">
                    {formData.title.length}/100 characters
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">Message *</Form.Label>
                  <Form.Control 
                    as="textarea"
                    rows={5}
                    placeholder="Enter your notification message here..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    required
                    maxLength={500}
                  />
                  <Form.Text className="text-muted">
                    {formData.message.length}/500 characters
                  </Form.Text>
                </Form.Group>

                {/* Preview */}
                {(formData.title || formData.message) && (
                  <Alert variant="info" className="mb-4">
                    <div className="d-flex align-items-start">
                      <span style={{ fontSize: '24px', marginRight: '12px' }}>
                        {iconOptions.find(opt => opt.value === formData.icon)?.emoji || '🔔'}
                      </span>
                      <div className="flex-grow-1">
                        <strong className="d-block mb-1">{formData.title || 'Title preview'}</strong>
                        <small className="text-muted d-block">
                          {formData.message || 'Message preview'}
                        </small>
                      </div>
                    </div>
                  </Alert>
                )}

                <Button 
                  type="submit" 
                  variant="primary"
                  className="w-100"
                  disabled={loading || !formData.title.trim() || !formData.message.trim()}
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Sending...
                    </>
                  ) : (
                    '📢 Send Notification to All Users'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Broadcasts */}
        <Col lg={6} className="mb-4">
          <Card className="shadow-sm border-0">
            <Card.Body className="p-4">
              <h5 className="mb-4">Recent Broadcasts</h5>
              
              {fetchingHistory ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <>
                  {/* Platform Updates */}
                  <div className="mb-4">
                    <h6 className="text-muted mb-3">
                      <Badge bg="info" className="me-2">Platform Updates</Badge>
                    </h6>
                    {recentBroadcasts.platform.length === 0 ? (
                      <p className="text-muted small">No platform updates sent yet</p>
                    ) : (
                      <div className="list-group">
                        {recentBroadcasts.platform.map((notif) => (
                          <div 
                            key={notif._id} 
                            className="list-group-item border-0 border-bottom px-0"
                          >
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <strong className="d-block mb-1" style={{ fontSize: '0.9rem' }}>
                                  {notif.title}
                                </strong>
                                <p className="mb-1 text-muted small" style={{ fontSize: '0.85rem' }}>
                                  {notif.message.substring(0, 80)}
                                  {notif.message.length > 80 && '...'}
                                </p>
                                <small className="text-muted">
                                  {formatDate(notif.createdAt)}
                                </small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Admin Notifications */}
                  <div>
                    <h6 className="text-muted mb-3">
                      <Badge bg="warning" text="dark" className="me-2">Admin Notifications</Badge>
                    </h6>
                    {recentBroadcasts.admin.length === 0 ? (
                      <p className="text-muted small">No admin notifications sent yet</p>
                    ) : (
                      <div className="list-group">
                        {recentBroadcasts.admin.map((notif) => (
                          <div 
                            key={notif._id} 
                            className="list-group-item border-0 border-bottom px-0"
                          >
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <strong className="d-block mb-1" style={{ fontSize: '0.9rem' }}>
                                  {notif.title}
                                </strong>
                                <p className="mb-1 text-muted small" style={{ fontSize: '0.85rem' }}>
                                  {notif.message.substring(0, 80)}
                                  {notif.message.length > 80 && '...'}
                                </p>
                                <small className="text-muted">
                                  {formatDate(notif.createdAt)}
                                </small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SendNotifications;